import { START_PAGE } from "@/constants/pagination";
import { ORG_SELECT_DEFAULT_COLUMN_ORDER, ORG_SELECT_DEFAULT_VISIBLE_COLUMNS } from "@/features/session/constants/organizationSelectColumns";
import { TOrganizationSelectFilterCache } from "@/features/session/types/organizationSelectFilter";
import { FCC } from "@/types/common";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

export const ORGANIZATION_SELECT_FILTER_STORAGE_KEY = "organizationSelectFilterCache";

export const DEFAULT_ORGANIZATION_SELECT_FILTER: TOrganizationSelectFilterCache = {
  page: START_PAGE,
  search: "",
  externalId: "",
  name: "",
  productId: "",
  lastLoginFrom: null,
  lastLoginTo: null,
  createdDateFrom: null,
  createdDateTo: null,
  visibleColumns: ORG_SELECT_DEFAULT_VISIBLE_COLUMNS,
  columnOrder: ORG_SELECT_DEFAULT_COLUMN_ORDER,
};

const loadFromStorage = (): TOrganizationSelectFilterCache => {
  try {
    const stored = sessionStorage.getItem(ORGANIZATION_SELECT_FILTER_STORAGE_KEY);
    if (!stored) return DEFAULT_ORGANIZATION_SELECT_FILTER;
    return { ...DEFAULT_ORGANIZATION_SELECT_FILTER, ...JSON.parse(stored) };
  } catch {
    return DEFAULT_ORGANIZATION_SELECT_FILTER;
  }
};

type TOrganizationSelectFilterCacheContext = {
  filterCache: TOrganizationSelectFilterCache;
  setFilterCache: (value: TOrganizationSelectFilterCache) => void;
};

const OrganizationSelectFilterCacheContext = createContext<TOrganizationSelectFilterCacheContext>({
  filterCache: DEFAULT_ORGANIZATION_SELECT_FILTER,
  setFilterCache: () => {},
});

export const OrganizationSelectFilterCacheProvider: FCC = ({ children }) => {
  const [filterCache, setFilterCacheState] = useState<TOrganizationSelectFilterCache>(loadFromStorage);

  const setFilterCache = useCallback((value: TOrganizationSelectFilterCache) => {
    setFilterCacheState(value);
    sessionStorage.setItem(ORGANIZATION_SELECT_FILTER_STORAGE_KEY, JSON.stringify(value));
  }, []);

  const value = useMemo(() => ({ filterCache, setFilterCache }), [filterCache, setFilterCache]);

  return <OrganizationSelectFilterCacheContext.Provider value={value}>{children}</OrganizationSelectFilterCacheContext.Provider>;
};

export const useOrganizationSelectFilterCache = () => useContext(OrganizationSelectFilterCacheContext);
